import { EventEmitter, Injectable, Injector } from '@angular/core';
import { Relationship } from '../interfaces/relationship';
import { FilterType } from '../interfaces/filter-type';
import { find, without, uniq, uniqBy, clone, each, map } from 'lodash';
import { Observable } from 'rxjs/Observable';
import { Collection } from './sd-angular-jsonapi-collection';
import { EntityManager } from '../services/sd-angular-jsonapi-entity-manager';
import { JSONAPIError, JSONAPIErrorBag } from './sd-angular-jsonapi-error';
import { HttpErrorResponse } from '@angular/common/http';
import { ResourceLinks } from '../interfaces/resource-links';
import { JSON_API_ERROR_MESSAGES } from './error-messages';
import { PageBasedPaginationOptions } from '../interfaces/page-based-pagination-options';
import { OffsetBasedPaginationOptions } from '../interfaces/offset-based-pagination-options';
import { CursorBasedPaginationOptions } from '../interfaces/cursor-based-pagination-options';
import { IJSONAPIResource } from '../interfaces/jsonapi-resource';
import { OrderType, SortQuery } from '../interfaces/sort-query';

@Injectable()
export abstract class JSONAPIResource implements IJSONAPIResource {

	private _limit: number = null;

	private _paginate: any = { // TODO should be typed
		currentPage: 0,
		size: 1
	};

	private _doPaginate: boolean = false;

	private _related: Relationship[] = [];

	private _filters: FilterType[] = [];

	private _sortBy: SortQuery[] = [];

	private _fields: string[] = [];

	private _isLoaded: boolean = false;

	private _isNew: boolean = false;

	private _hasUpdate: boolean = false;

	private _forInitialization: any = null;

	private _initialized: boolean = false;

	private _loadRelationships: string[] = [];

	private _loadedRelationships: string[] = [];

	private _liveRelationships: EventEmitter<string> = new EventEmitter();

	private _links: ResourceLinks = null;

	public manager: EntityManager = null;

	public resource: string = null;

	public id: string | number = null;

	public attributes: string[] = [];

	public onUpdated: EventEmitter<boolean> = new EventEmitter();

	public onDeleted: EventEmitter<boolean> = new EventEmitter();

	public onCreated: EventEmitter<boolean> = new EventEmitter();

	public onRefreshed: EventEmitter<this | boolean> = new EventEmitter();

	constructor (data?: any, manager?: EntityManager) {
		if ( manager ) {
			this.manager = manager;
		}
		if ( data && this.manager ) {
			this.setData(data);
		} else if ( data && !this.manager ) {
			this._forInitialization = data;
		} else {

		}
	}

	initialize (): void {
		if ( !this._initialized ) {
			if ( this._forInitialization && this.manager ) {
				this.setData(this._forInitialization);
			} else {
				if ( !this.manager ) {
					throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_ENTITY_MANAGER);
				}
			}
			this._initialized = true;
			this._forInitialization = null;
		} else {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.ALREADY_INITIALIZED);
		}
	}

	setData (data?): void {
		if ( data ) {
			Object.assign(this, data);
		}
		this.manager.request.registerResource(this.constructor.name, this.constructor);

		this._liveRelationships.subscribe((relationship: string) => {
			this[ relationship ] = () => {
				const relatedData = find(this._related, { name: relationship });
				let resourceInstance = this.manager.request.getResourceInstance(relatedData.name);
				if ( resourceInstance ) {
					if ( relatedData ) {
						return new Collection(relatedData, null, resourceInstance.instance, this.manager);
					} else {
						return new Collection([], null, resourceInstance.instance, this.manager);
					}
				} else {
					return relatedData;
				}
			};
		});
	}

	refresh (): void {
		if ( this.hasSelfLink() || this.hasRelatedLink() ) {
			const url = this.hasSelfLink() ? this._links.self : this._links.related;
			this.manager.request.sendWithUrl(url).subscribe(
				(success: any) => { // TODO should be typed
					const data = success.data;
					if ( data ) {
						for ( const attribute in this.attributes ) {
							if ( data.hasOwnProperty(attribute) ) {
								this[ attribute ] = data[ attribute ];
							}
						}
						this.onRefreshed.emit(<this>this);
					}
				},
				(error) => {
					// TODO error handling
					this.onRefreshed.emit(<boolean>false);
				}
			);
		}
	}

	private hasSelfLink (): boolean {
		return !!(this._links && this._links.self);
	}

	private hasRelatedLink (): boolean {
		return !!(this._links && this._links.related);
	}

	reload () {
		this.refresh();
	}

	addRelationship (resource: string, links: ResourceLinks, data: any): this {
		this._related.push(<Relationship>{ name: resource, links, data });
		return this;
	}

	addRelationships (resources: Relationship[]): this {
		for ( const resource of resources ) {
			this.addRelationship(resource.name, resource.links, resource.data);
		}
		return this;
	}

	hasLoadedRelationship (name: string): this {
		this._loadedRelationships.push(name);
		return this;
	}

	associateRelationshipData (name: string, data: any, callback?: any): void {
		const instanceName = this.manager.request.getResourceInstance(name);
		if ( instanceName ) {
			const relationship = find(this._related, (relatedElement: any) => {
				return relatedElement.name === name;
			});
			const relationshipData = find(relationship.data, { id: data.id });
			if ( relationshipData && data && data.attributes ) {
				Object.assign(
					relationshipData,
					data.attributes,
					{ instance: new (instanceName.instance as any)(relationshipData) },
					{ attributes: data.attributes }
				);
			}
			this.emitLiveRelationship(name);
			if ( typeof callback === 'function' ) {
				callback(this);
			}
		} else {
			this.findRelationshipData(name, data);
			this.emitLiveRelationship(name);
			if ( typeof callback === 'function' ) {
				callback(this);
			}
		}
	}

	findRelationshipData (name: string, data: any): any {
		const relationship = find(this._related, (relatedElement: any) => {
			return relatedElement.name === name;
		});
		const relationshipData = find(relationship.data, { id: data.id });
		if ( relationshipData && data && data.attributes ) {
			Object.assign(relationshipData, data.attributes);
			return relationshipData;
		}
	}

	private emitLiveRelationship (name: string) {
		this._liveRelationships.emit(name);
	}

	all (fields?: string[]): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		if ( fields ) {
			this.only(fields);
		}
		return new Observable((observer) => {
			const url = this.getURLAppendix();
			this.manager.request.send(this.resource, {}, url).subscribe(
				(success: any) => {
					if ( success && success.data ) {
						observer.next(new Collection(success, success.included, this.constructor, this.manager));
					} else {
						observer.next(new Collection([], [], this.constructor, this.manager));
					}
				},
				(error: JSONAPIErrorBag | HttpErrorResponse) => {
					observer.error(error);
				}
			);
		});
	}

	get (id?: number | string, fields?: string[]): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		if ( !id ) {
			return this.all(fields);
		} else {
			if ( fields ) {
				this.only(fields);
			}
			return new Observable((observer) => {
				const url = [ id.toString(), this.getURLAppendix() ];
				this.manager.request.send(this.resource, {}, url).subscribe(
					(success: any) => {
						if ( success && success.data ) {
							observer.next(
								new Collection(success, success.included, this.constructor, this.manager));
						} else {
							observer.next(new Collection([], [], this.constructor, this.manager));
						}
					},
					(error: JSONAPIErrorBag | HttpErrorResponse) => {
						observer.error(error);
					}
				);
			});
		}
	}

	find (id: number | string): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		return new Observable((observer) => {
			const url = [ id.toString(), this.getURLAppendix() ];
			this.manager.request.send(this.resource, {}, url).subscribe(
				(success: any) => {
					if ( success && success.data ) {
						observer.next(new Collection(success, success.included, this.constructor, this.manager));
					} else {
						observer.next(new Collection([], [], this.constructor, this.manager));
					}
				},
				(error: JSONAPIErrorBag | HttpErrorResponse) => {
					observer.error(error);
				}
			);
		});
	}

	update (): Observable<Collection | JSONAPIResource | JSONAPIErrorBag> {
		if ( this._isLoaded ) {
			if ( this.canUpdate() ) {
				const url = [ this.id.toString() ];
				const attributes = {};
				for ( const a of this.attributes ) {
					if ( a !== 'id' ) {
						attributes[ a ] = this[ a ];
					}
				}
				const data: any = { // TODO request should be typed
					data: {
						attributes: attributes,
						type: this.resource,
						id: this.id
					}
				};
				return new Observable((observer) => {
					this.manager.request.send(this.resource, data, url, 'patch').subscribe(
						(success: any) => {
							if ( success && success.data ) {
								observer.next(
									new Collection(success, success.included, this.constructor, this.manager));
								this.onUpdated.emit(true);
							} else {
								observer.next(new Collection([], [], this.constructor, this.manager));
							}
						},
						(error: JSONAPIErrorBag | HttpErrorResponse) => {
							observer.error(error);
							this.onUpdated.emit(false);
						}
					);
				});
			} else {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.CANNOT_UPDATE_WITHOUT_ID);
			}
		} else {
			const attributes = {};
			for ( const a of this.attributes ) {
				if ( a !== 'id' ) {
					attributes[ a ] = this[ a ];
				}
			}
			const data: any = { // TODO request should be typed
				data: {
					attributes: attributes,
					type: this.resource
				}
			};
			return new Observable((observer) => {
				this.manager.request.send(this.resource, data, [], 'post').subscribe(
					(success: any) => {
						if ( success && success.data ) {
							observer.next(
								new Collection(success, success.included, this.constructor, this.manager));
							this.onCreated.emit(true);
						} else {
							observer.next(new Collection([], [], this.constructor, this.manager));
							this.onCreated.emit(false);
						}
					},
					(error: JSONAPIErrorBag | HttpErrorResponse) => {
						observer.error(error);
					}
				);
			});
		}
	}

	private canUpdate (): boolean {
		return !!this.id;
	}

	delete (): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		if ( this.canDelete() ) {
			if ( this._links && this._links.self ) {
				return new Observable((observer) => {
					this.manager.request.sendWithUrl(this._links.self, {}, 'delete').subscribe(
						(success: any) => {
							if ( success && success.data ) {
								observer.next(
									new Collection(success, success.included, this.constructor, this.manager)
								);
								this.onDeleted.emit(true);
							} else {
								observer.next(new Collection([], [], this.constructor, this.manager));
								this.onDeleted.emit(false);
							}
						},
						(error: JSONAPIErrorBag | HttpErrorResponse) => {
							observer.error(error);
						}
					);
				});
			} else {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_SELF_LINK);
			}
		} else {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.CANNOT_DELETE_WITHOUT_ID);
		}
	}

	private canDelete (): boolean {
		return !!this.id;
	}

	create (data: any, autoSave: boolean = true): this | Observable<Collection | JSONAPIResource | JSONAPIErrorBag> {
		if ( this._isLoaded ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.CANNOT_CREATE_FROM_EXISTING);
		}
		if ( data ) {
			this.attributes = Object.keys(data);
			for ( const attribute of this.attributes ) {
				this[ attribute ] = data[ attribute ];
			}
		}
		return autoSave ? <Observable<Collection | JSONAPIResource | JSONAPIErrorBag>>this.save() : <this>this;
	}

	new (data: any): this | any {
		return this.create(data, false);
	}

	save (): Observable<Collection | JSONAPIResource | any> {
		return this.update();
	}

	load (relationships: string | string[]): void {
		const loadRelationship = (related: string) => {
			const relationship = find(this._related, { name: related });
			if ( relationship && relationship.links && relationship.links.related ) {
				const url = relationship.links.related;
				this.manager.request.sendWithUrl(url).subscribe(
					(success: any) => {
						if ( success && success.data && Array.isArray(success.data) ) {
							for ( const result of success.data ) {
								this.associateRelationshipData(related, result, (ref: this) => {
									ref.onUpdated.emit(true);
								});
							}
						} else {
							// checking if the returned result is a one-to-one relationship,
							// in this case we can deliver the data immediately
							if ( success && success.data && success.data.attributes ) {
								this.associateRelationshipData(related, success.data, (ref: this) => {
									ref.onUpdated.emit(true);
								});
							}
						}
					},
					(error: any) => {
						// TODO error handling
					}
				);
			} else {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NOT_A_VALID_RELATIONSHIP + relationships);
			}
		};

		if ( Array.isArray(relationships) ) {
			for ( const relationship of relationships ) {
				loadRelationship(relationship);
			}
		} else {
			loadRelationship(relationships);
		}
	}

	paginate (pageSize: number = 1, startAtPage: number = 0): this {
		if ( this._limit ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.PAGINATE_AND_LIMIT_CONFLICT);
		}
		if ( this.manager.paginationStrategySet() && this.manager.paginationOptionsSet() ) {
			this._paginate.size = pageSize;
			this._paginate.currentPage = startAtPage;
			this._doPaginate = true;
			return this;
		} else {
			if ( !this.manager.paginationStrategySet() ) {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_PAGINATION_STRATEGY_SET);
			} else {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_PAGINATION_OPTIONS_SET);
			}
		}
	}

	limit (limit: number = 1): this {
		if ( this._doPaginate ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.PAGINATE_AND_LIMIT_CONFLICT);
		}
		if ( this.manager.paginationStrategySet() && this.manager.paginationOptionsSet() ) {
			this._limit = limit;
			return this;
		} else {
			if ( !this.manager.paginationStrategySet() ) {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_PAGINATION_STRATEGY_SET);
			} else {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NO_PAGINATION_OPTIONS_SET);
			}
		}
	}

	take (amount: number = 1): this {
		return this.limit(amount);
	}

	with (related: string | string[]): this {
		if ( !Array.isArray(related) ) {
			this._loadRelationships.push(related);
		} else {
			this._loadRelationships.push(...related);
		}
		return this;
	}

	only (fields: string[]): this {
		this._fields = fields;
		return this;
	}

	fields (fields: string[]): this {
		return this.only(fields);
	}

	filter (attribute: string, value: string | boolean | number): this {
		if ( !Array.isArray(this._filters) ) {
			this._filters = [];
		}
		this._filters.push({ attribute, value });
		return this;
	}

	filters (filters: [ string, string | boolean | number ][]): this {
		if ( !Array.isArray(this._filters) ) {
			this._filters = [];
		}
		for ( const filter of filters ) {
			this._filters.push({ attribute: filter[ 0 ], value: filter[ 1 ] });
		}
		return this;
	}

	private isSortQuery (element: any): element is SortQuery {
		return (typeof element === 'object') && element.on && element.order;
	}

	sortBy (direction: OrderType, attribute: string): this {
		this._sortBy.push({ dir: direction, on: attribute });
		return this;
	}

	private constructFilters (): string[] {
		if ( !Array.isArray(this._filters) ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.UNEXPECTED_FILTERS_TYPE + typeof this._filters);
		}
		const filterQuery: string[] = [];
		for ( const filter of this._filters ) {
			filterQuery.push(`filter[${filter.attribute}]=${filter.value}`);
		}
		return filterQuery;
	}

	private constructRelationships (): string {
		if ( !Array.isArray(this._loadRelationships) ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.UNEXPECTED_RELATIONSHIPS_TYPE + typeof this._loadRelationships);
		}
		if ( this._loadRelationships.length > 0 ) {
			let relationshipQuery = 'include=';
			for ( const relationship of this._loadRelationships ) {
				relationshipQuery += relationship;
			}
			return relationshipQuery;
		} else {
			return '';
		}
	}

	// TODO this currently only supports 1 level of field filters,
	// TODO for resources with relationships, those would be ignored at this time (or even error out)
	// TODO need a typed interface that either allows single string (which relates to fields on the same resource)
	// TODO or an object with name <=> value pair or an array with two string values
	// TODO or an array with a class that extends from JSONAPIResource and a string value
	// TODO also need the option to add multiple filters at once
	private constructFieldsFilter (): string {
		if ( !Array.isArray(this._fields) ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.UNEXPECTED_FIELDS_FILTERS_TYPE + typeof this._fields);
		}
		if ( this._fields.length > 0 ) {
			let fieldsFilterQuery = `fields[${this.resource}]`;
			for ( const field of this._fields ) {
				fieldsFilterQuery += `,${field}`;
			}
			return fieldsFilterQuery.slice(1);
		} else {
			return '';
		}
	}

	private constructLimitQuery (): string {
		if ( this._limit ) {
			if ( this.manager.getPaginationStrategy() === 'page' ) {
				const pageNumberAttribute = (<PageBasedPaginationOptions>this.manager.getPaginationOptions()).pageNumberAttribute;
				const pageSizeAttribute = (<PageBasedPaginationOptions>this.manager.getPaginationOptions()).pageSizeAttribute;
				return `page[${pageNumberAttribute}]=0&page[${pageSizeAttribute}]=${this._limit}`;
			} else if ( this.manager.getPaginationStrategy() === 'offset' ) {
				const pageOffsetAttribute = (<OffsetBasedPaginationOptions>this.manager.getPaginationOptions()).pageOffsetAttribute;
				const pageLimitAttribute = (<OffsetBasedPaginationOptions>this.manager.getPaginationOptions()).pageLimitAttribute;
				return `page[${pageOffsetAttribute}]=0&page[${pageLimitAttribute}]=${this._limit}`;
			} else {
				const pageCursorAttribute = (<CursorBasedPaginationOptions>this.manager.getPaginationOptions()).pageCursorAttribute;
				const pageLimitAttribute = (<CursorBasedPaginationOptions>this.manager.getPaginationOptions()).pageLimitAttribute;
				return `page[${pageCursorAttribute}]=0&page[${pageLimitAttribute}]=${this._limit}`;
			}
		}
		return '';
	}

	private constructPaginationQuery (): string {
		if ( this._doPaginate ) {
			if ( this.manager.getPaginationStrategy() === 'page' ) {
				const pageNumberAttribute = (<PageBasedPaginationOptions>this.manager.getPaginationOptions()).pageNumberAttribute;
				const pageSizeAttribute = (<PageBasedPaginationOptions>this.manager.getPaginationOptions()).pageSizeAttribute;
				return `page[${pageNumberAttribute}]=${this._paginate.currentPage}&page[${pageSizeAttribute}]=${this._paginate.size}`;
			} else if ( this.manager.getPaginationStrategy() === 'offset' ) {
				const pageOffsetAttribute = (<OffsetBasedPaginationOptions>this.manager.getPaginationOptions()).pageOffsetAttribute;
				const pageLimitAttribute = (<OffsetBasedPaginationOptions>this.manager.getPaginationOptions()).pageLimitAttribute;
				return `page[${pageOffsetAttribute}]=${this._paginate.currentPage}&page[${pageLimitAttribute}]=${this._paginate.size}`;
			} else {
				const pageCursorAttribute = (<CursorBasedPaginationOptions>this.manager.getPaginationOptions()).pageCursorAttribute;
				const pageLimitAttribute = (<CursorBasedPaginationOptions>this.manager.getPaginationOptions()).pageLimitAttribute;
				return `page[${pageCursorAttribute}]=${this._paginate.currentPage}&page[${pageLimitAttribute}]=${this._paginate.size}`;
			}
		}
		return '';
	}

	private constructSortFilter (): string {
		if ( this._sortBy ) {
			let sortQuery = 'sort=';
			if ( !Array.isArray(this._sortBy) ) {
				throw new JSONAPIError(JSON_API_ERROR_MESSAGES.UNEXPECTED_SORT_FILTERS_TYPE + typeof this._sortBy);
			}
			sortQuery += map(each(clone(this._sortBy), (el: SortQuery) => {
				if ( el.dir === 'desc' ) {
					el.on = `-${el.on}`;
				}
			}), 'on').join(',');
			return sortQuery;
		}
		return '';
	}

	private getURLAppendix (): string {
		let pagination: string;
		if ( this._limit ) {
			pagination = this.constructLimitQuery();
		} else if ( !this._limit && this._paginate ) {
			pagination = this.constructPaginationQuery();
		} else if ( this._limit && this._doPaginate ) {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.PAGINATE_AND_LIMIT_CONFLICT);
		} else {
			pagination = '';
		}
		const appendices = [
			...this.constructFilters(),
			this.constructRelationships(),
			this.constructFieldsFilter(),
			pagination,
			this.constructSortFilter()
		].filter(x => x).join('&');
		if ( appendices.length > 0 ) {
			return '?' + appendices;
		} else {
			return '';
		}
	}
}
